import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
export interface WorldClock {
  city: string;
  time: string; // HH:MM
  utcOffset: number;
  isReference?: boolean;
}
export interface WorldClockPanelProps {
  clocks: WorldClock[];
  label?: string;
  mode?: 'stimulus' | 'support';
}
export function WorldClockPanel({
  clocks,
  label,
  mode = 'support'
}: WorldClockPanelProps) {
  const isStimulus = mode === 'stimulus';
  // Sort clocks so reference is first, or keep original order if no reference
  const sortedClocks = [...clocks].sort((a, b) => {
    if (a.isReference && !b.isReference) return -1;
    if (!a.isReference && b.isReference) return 1;
    return 0;
  });
  const formatOffset = (offset: number) => {
    return offset >= 0 ? `UTC +${offset}` : `UTC ${offset}`;
  };
  const calculateDifference = (clock1: WorldClock, clock2: WorldClock) => {
    const diff = clock2.utcOffset - clock1.utcOffset;
    return diff > 0 ? `+${diff} uur` : `${diff} uur`;
  };
  return <div className={`relative overflow-hidden ${isStimulus ? 'bg-sky-50 rounded-xl px-4 pt-4 pb-6 border border-sky-200' : 'bg-gradient-to-br from-sky-50 to-blue-50 p-6 rounded-2xl border-2 border-sky-200 shadow-sm'}`} aria-label="Wereldklokken paneel" data-id="element-14833" data-mp="3ouen186js55k">
      
      {/* Subtle Globe Background */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03] flex items-center justify-center" data-id="element-14834" data-mp="3a5ihbhryr484">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" className="w-full h-full max-w-md" data-id="element-14835" data-mp="bnmnvn3r9ji3">
          
          <circle cx="12" cy="12" r="10" data-id="element-14836" data-mp="6ot81o0zxger" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" data-id="element-14837" data-mp="1p6yppoagkb8" />
          <path d="M2 12h20" data-id="element-14838" data-mp="3ttiv1vhqjwub" />
        </svg>
      </div>

      <div className="relative z-10" data-id="element-14839" data-mp="3tv2chpf4odxo">
        {label && <p className={`text-sm font-semibold text-sky-800 mb-6 text-center ${!isStimulus ? 'text-base' : ''}`} data-id="element-14840" data-mp="2mfo6f5r7fpqi">
          
            {label}
          </p>}

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8" data-id="element-14841" data-mp="2ig6zs901kmjr">
          {sortedClocks.map((clock, index) => <Fragment key={clock.city} data-id="element-14842" data-mp="2oumbnopilx0a" data-mpi={index}>
              <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.1
          }} className={`flex flex-col items-center p-4 rounded-xl min-w-[120px] ${clock.isReference ? 'bg-white border-2 border-sky-400 shadow-md' : 'bg-white/80 border border-sky-200 shadow-sm'}`} data-id="element-14843" data-mp="2otko4krbbi1o">
              
                <span className="text-sm font-medium text-slate-500 mb-1" data-id="element-14844" data-mp="25xbec9ng1dnh">
                  {clock.city}
                </span>
                <span className="text-2xl font-bold text-slate-800 font-mono tracking-wider mb-2" data-id="element-14845" data-mp="1w55ksvhjmo66">
                  {clock.time}
                </span>
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${clock.isReference ? 'bg-sky-100 text-sky-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-14846" data-mp="1i65uljedu17w">
                
                  {formatOffset(clock.utcOffset)}
                </span>
              </motion.div>
              {/* Arrow between clocks */}
              {index < sortedClocks.length - 1 && <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: index * 0.1 + 0.2
          }} className="flex flex-col items-center text-sky-400" data-id="element-14847" data-mp="1ck53nwqr8w70">
              
                  {!isStimulus && <span className="text-xs font-bold text-sky-600 mb-1 whitespace-nowrap" data-id="element-14848" data-mp="1hhnfbuw5ppip">
                      {calculateDifference(clock, sortedClocks[index + 1])}
                    </span>}
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="hidden sm:block" data-id="element-14849" data-mp="1r89q9bsxhrv2">
                
                    <line x1="5" y1="12" x2="19" y2="12" data-id="element-14850" data-mp="20a98u8jld404"></line>
                    <polyline points="12 5 19 12 12 19" data-id="element-14851" data-mp="2kbkgl1yu9n98"></polyline>
                  </svg>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="block sm:hidden" data-id="element-14852" data-mp="2ocw9bgd0eoln">
                
                    <line x1="12" y1="5" x2="12" y2="19" data-id="element-14853" data-mp="2y2fvovzxl0l9"></line>
                    <polyline points="19 12 12 19 5 12" data-id="element-14854" data-mp="2y1xsffeupm3u"></polyline>
                  </svg>
                </motion.div>}
            </Fragment>)}
        </div>

        {!isStimulus && sortedClocks.length >= 2 && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.5
      }} className="mt-6 bg-white/80 rounded-xl p-4 border border-sky-100 text-sm text-slate-700" data-id="element-14855" data-mp="25ebm3ze8o3da">
          
            <div className="flex items-start gap-2" data-id="element-14856" data-mp="3lstd7skw3h1l">
              <span className="text-lg" data-id="element-14857" data-mp="3ls1uvjgazft9">🌍</span>
              <div data-id="element-14858" data-mp="3qqv5930amqp7">
                <p className="font-semibold text-sky-800 mb-1" data-id="element-14859" data-mp="3qqbldgr31s8j">
                  Tijdverschil berekenen
                </p>
                <p data-id="element-14860" data-mp="1f39i67b0mbee">
                  Naar het <strong data-id="element-14861" data-mp="1a4y8pxomu2wp">oosten</strong> (later op de dag) = uren
                  erbij optellen (+).
                  <br data-id="element-14862" data-mp="15dydqxz2puwu" />
                  Naar het <strong data-id="element-14863" data-mp="15d6ijo9e1z73">westen</strong> (vroeger op de dag) = uren
                  eraf halen (−).
                </p>
              </div>
            </div>
          </motion.div>}
      </div>
    </div>;
}